import { defineStore } from "pinia";

import cargoUnitsApi from "@/services/api/cargoUnitsApi";

import { getCompanyId } from "@/utils/userUtils";

export const useCargoUnitStore = defineStore("cargoUnitStore", {
  state: () => ({
    units: [],
    companyId: getCompanyId(),
    isLoading: false,
    error: null,

    pagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
    },

    selectItems: [],
    selectSearchQuery: "",
    selectPagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
    },
    selectIsLoading: false,
  }),

  getters: {
    activeUnits: (state) =>
      state.units.filter((u) => u.enabled !== false),
  },

  actions: {
    async getUnitsRequest(params = {}) {
      this.isLoading = true;
      try {
        const response = await cargoUnitsApi.index(
          this.companyId,
          params
        );
        this.units = response.data.result || response.data || [];

        const meta = response.data.pagination_meta;
        if (meta) {
          this.pagination = {
            page: meta.page,
            pages: meta.pages,
            series: meta.series,
            next: meta.next,
            prev: meta.prev,
            count: meta.count,
            limit: meta.limit,
          };
        }
      } catch (error) {
        this.error = error;
        console.error(
          "Ошибка при получении единиц измерения:",
          error
        );
      } finally {
        this.isLoading = false;
      }
    },

    async showUnit(id) {
      try {
        const response = await cargoUnitsApi.show(this.companyId, id);
        return response.data.result || response.data || {};
      } catch (error) {
        this.error = error;
        console.error(
          "Ошибка при получении единицы измерения:",
          error
        );
      }
    },

    async createUnit(payload) {
      this.isLoading = true;
      try {
        const response = await cargoUnitsApi.create(
          this.companyId,
          payload
        );
        await this.getUnitsRequest();
        return response.data.result || response.data || {};
      } catch (error) {
        console.error(
          "Ошибка при создании единицы измерения:",
          error
        );
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async updateUnit(id, payload) {
      this.isLoading = true;
      try {
        await cargoUnitsApi.update(this.companyId, id, payload);
        await this.getUnitsRequest();
      } catch (error) {
        console.error(
          "Ошибка при обновлении единицы измерения:",
          error
        );
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async unitEnable(id) {
      this.isLoading = true;
      try {
        await cargoUnitsApi.enable(this.companyId, id);
        await this.getUnitsRequest();
      } catch (error) {
        console.error(
          "Ошибка при включении единицы измерения:",
          error
        );
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async unitDisable(id) {
      this.isLoading = true;
      try {
        await cargoUnitsApi.disable(this.companyId, id);
        await this.getUnitsRequest();
      } catch (error) {
        console.error(
          "Ошибка при отключении единицы измерения:",
          error
        );
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchSelectItems(payload = {}) {
      this.selectSearchQuery = payload.search || "";
      return await this.getSelectItemsRequest(1);
    },

    async loadMoreSelectItems() {
      if (this.selectIsLoading || !this.selectPagination.next) return;
      const nextPage = this.selectPagination.page + 1;
      return await this.getSelectItemsRequest(nextPage);
    },

    async getSelectItemsRequest(page) {
      const pageNumber = parseInt(page) || 1;
      this.selectIsLoading = true;
      try {
        const params = {
          page: pageNumber,
          enabled: true,
        };

        if (this.selectSearchQuery) {
          params["q[search]"] = this.selectSearchQuery;
        }

        const response = await cargoUnitsApi.index(
          this.companyId,
          params
        );
        const result = response.data.result || response.data || [];

        if (pageNumber === 1) {
          this.selectItems = result;
        } else {
          this.selectItems = [...this.selectItems, ...result];
        }

        const meta = response.data.pagination_meta;
        if (meta) {
          this.selectPagination = {
            page: meta.page,
            pages: meta.pages,
            series: meta.series,
            next: meta.next,
            prev: meta.prev,
            count: meta.count,
            limit: meta.limit,
          };
        }
      } catch (error) {
        console.error(
          "Ошибка при загрузке единиц измерения для селекта:",
          error
        );
      } finally {
        this.selectIsLoading = false;
      }
    },

    clearSelectItems() {
      this.selectItems = [];
      this.selectSearchQuery = "";
      this.selectPagination = {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      };
    },
  },
});
